import React from 'react';
import { api } from '../lib/api';
import { useIsMobile } from '../hooks/useIsMobile';
import { Badge, Btn } from './index';
import { TestPaymentsNote } from './TestPaymentsNote';
import { formatInTz, DEFAULT_TIMEZONE } from '../lib/timezone';
import { palette as T, type } from '../design';

// Admin's booking payments (JTP-24): every booking payment, newest first, 50 per page, and one
// payment with the bookings it covers. A refund flag shows in full, so admin knows who to refund
// by hand. The labels come from the server (REFUND_REASON_LABEL); this panel never words them.

const ADMIN_TZ = DEFAULT_TIMEZONE;

// What admin reads for each outcome: JTPay's own outcome names (glossary: Outcome).
export const OUTCOME_LABEL = {
  waiting: 'Waiting',
  confirming: 'Confirming',
  paid: 'Paid',
  underpaid: 'Underpaid',
  overpaid: 'Overpaid',
  paid_multiple: 'Paid more than once',
  expired: 'Expired',
  failed: 'Failed',
};
const OUTCOME_BADGE = { paid: 'sage', overpaid: 'sage', paid_multiple: 'sage', waiting: 'gold', confirming: 'gold', underpaid: 'terracotta' };
const STATUS_BADGE = { pending: 'gold', confirmed: 'sage' };

// Pesos as the API sends them ('2000.00') -> '₱2,000.00'. Null stays a dash: nothing received yet.
export function pesos(value) {
  if (value == null) return '—';
  return '₱' + Number(value).toLocaleString('en-PH', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
}

const FONT = "'Plus Jakarta Sans', sans-serif";
const cardStyle = { background: T.white, borderRadius: 20, border: '1px solid ' + T.border };
const cellStyle = { padding: '12px 16px', fontFamily: FONT, fontSize: type.body, color: T.walnut, verticalAlign: 'top' };
const headStyle = { fontFamily: FONT, fontWeight: 700, fontSize: type.caption, color: T.driftwood, textTransform: 'uppercase', letterSpacing: 0.8, padding: '12px 16px', textAlign: 'left', borderBottom: '1px solid ' + T.border };
const noteStyle = { fontFamily: FONT, fontSize: type.body, color: T.driftwood, margin: 0 };

// The refund flag, in full and wrapping on a phone. Nothing when no money is owed back.
function RefundFlag({ label }) {
  if (!label) return null;
  return (
    <span style={{ display: 'inline-block', fontFamily: FONT, fontSize: type.label, fontWeight: 600, color: T.errorRed, background: T.ivory, border: '1px solid ' + T.errorRed, borderRadius: 10, padding: '4px 10px', whiteSpace: 'normal' }}>
      {label}
    </span>
  );
}

function ErrorBox({ message, onRetry }) {
  return (
    <div role="alert" style={{ ...cardStyle, borderRadius: 16, display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 12, border: '1px solid ' + T.errorRed, padding: 16, marginBottom: 16 }}>
      <p style={{ ...noteStyle, color: T.errorRed, flex: '1 1 200px' }}>{message}</p>
      <Btn size="sm" variant="ghost" onClick={onRetry}>Try again</Btn>
    </div>
  );
}

function Table({ headers, minWidth, children }) {
  return (
    <div style={{ overflowX: 'auto' }}>
      <table style={{ width: '100%', borderCollapse: 'collapse', minWidth }}>
        <thead>
          <tr style={{ background: T.ivory }}>
            {headers.map((h) => <th key={h} style={headStyle}>{h}</th>)}
          </tr>
        </thead>
        <tbody>{children}</tbody>
      </table>
    </div>
  );
}

// Load a path, with loading and error state. `reloadKey` changes to load it again.
function useAdminGet(path, failMessage) {
  const [state, setState] = React.useState({ data: null, loading: true, error: null });
  const [reloadKey, setReloadKey] = React.useState(0);
  React.useEffect(() => {
    let cancelled = false;
    setState((s) => ({ ...s, loading: true, error: null }));
    api.get(path)
      .then((data) => { if (!cancelled) setState({ data, loading: false, error: null }); })
      .catch((err) => {
        console.error('Admin booking payments fetch failed:', err);
        if (!cancelled) setState({ data: null, loading: false, error: err?.data?.message || failMessage });
      });
    return () => { cancelled = true; };
  }, [path, failMessage, reloadKey]);
  return { ...state, reload: () => setReloadKey((k) => k + 1) };
}

const LIST_FAILED = 'We could not load booking payments. Check your connection, then try again.';
const DETAIL_FAILED = 'We could not load this booking payment. Check your connection, then try again.';

export function AdminBookingPaymentsPanel() {
  const [openId, setOpenId] = React.useState(null);
  const [page, setPage] = React.useState(1);
  const [refundOnly, setRefundOnly] = React.useState(false);

  return (
    <div>
      {/* JTP-27: while JTPay's app key is a test key, admin reads that no real money moves. */}
      <TestPaymentsNote style={{ fontSize: type.body, margin: '0 0 12px' }} />
      {openId ? (
        <BookingPaymentDetail id={openId} onBack={() => setOpenId(null)} />
      ) : (
        <BookingPaymentList
          page={page}
          setPage={setPage}
          refundOnly={refundOnly}
          setRefundOnly={(v) => { setRefundOnly(v); setPage(1); }}
          onOpen={setOpenId}
        />
      )}
    </div>
  );
}

function BookingPaymentList({ page, setPage, refundOnly, setRefundOnly, onOpen }) {
  const path = '/admin/booking-payments?page=' + page + (refundOnly ? '&refund=1' : '');
  const { data, loading, error, reload } = useAdminGet(path, LIST_FAILED);
  const checkboxId = React.useId();
  const isMobile = useIsMobile();

  const payments = data?.bookingPayments || [];
  const total = data?.total || 0;
  const pageSize = data?.pageSize || 50;
  const lastPage = Math.max(1, Math.ceil(total / pageSize));
  const firstRow = total === 0 ? 0 : (page - 1) * pageSize + 1;
  const lastRow = Math.min(page * pageSize, total);

  return (
    <div>
      <div style={{ ...cardStyle, padding: 20, marginBottom: 16 }}>
        <label htmlFor={checkboxId} style={{ display: 'flex', alignItems: 'center', gap: 10, fontFamily: FONT, fontSize: type.body, fontWeight: 600, color: T.walnut, cursor: 'pointer' }}>
          <input id={checkboxId} type="checkbox" checked={refundOnly} onChange={(e) => setRefundOnly(e.target.checked)} />
          Only booking payments to refund by hand
        </label>
        <p style={{ ...noteStyle, fontSize: type.label, marginTop: 10 }}>Times are in {ADMIN_TZ} time. Newest booking payment first.</p>
      </div>

      {error && <ErrorBox message={error} onRetry={reload} />}

      <div style={{ ...cardStyle, overflow: 'hidden' }}>
        {isMobile ? payments.map((p) => (
          <article key={p.id} aria-label={'Booking payment ' + p.reference} style={{ padding: 16, borderBottom: '1px solid ' + T.border, display: 'flex', flexDirection: 'column', gap: 8 }}>
            <div style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', justifyContent: 'space-between', gap: 8 }}>
              <span style={{ fontWeight: 600, color: T.espresso, fontFamily: 'monospace', fontSize: type.body }}>{p.reference}</span>
              <Badge variant={OUTCOME_BADGE[p.outcome] || 'subtle'}>{OUTCOME_LABEL[p.outcome] || p.outcome}</Badge>
            </div>
            <p style={{ ...noteStyle, color: T.walnut }}>
              {p.studentName} · {p.bookingCount} {p.bookingCount === 1 ? 'booking' : 'bookings'}
            </p>
            <p style={{ ...noteStyle, color: T.walnut }}>Amount {pesos(p.amount)} · Received {pesos(p.received)}</p>
            <p style={{ ...noteStyle, fontSize: type.label }}>Started {formatInTz(p.createdAt, ADMIN_TZ)}</p>
            <RefundFlag label={p.refundLabel} />
            <div>
              <Btn size="sm" variant="ghost" onClick={() => onOpen(p.id)} aria-label={'Open booking payment ' + p.reference}>Open</Btn>
            </div>
          </article>
        )) : (
        <Table headers={['Reference', 'Student', 'Amount', 'Received', 'Outcome', 'Bookings', 'Refund', 'Started', '']} minWidth={980}>
          {payments.map((p) => (
            <tr key={p.id} style={{ borderBottom: '1px solid ' + T.border }}>
              <td style={{ ...cellStyle, fontWeight: 600, color: T.espresso, fontFamily: 'monospace' }}>{p.reference}</td>
              <td style={cellStyle}>{p.studentName}</td>
              <td style={cellStyle}>{pesos(p.amount)}</td>
              <td style={cellStyle}>{pesos(p.received)}</td>
              <td style={cellStyle}><Badge variant={OUTCOME_BADGE[p.outcome] || 'subtle'}>{OUTCOME_LABEL[p.outcome] || p.outcome}</Badge></td>
              <td style={cellStyle}>{p.bookingCount}</td>
              <td style={{ ...cellStyle, minWidth: 180 }}><RefundFlag label={p.refundLabel} /></td>
              <td style={{ ...cellStyle, fontSize: type.label, color: T.driftwood }}>{formatInTz(p.createdAt, ADMIN_TZ)}</td>
              <td style={cellStyle}>
                <Btn size="sm" variant="ghost" onClick={() => onOpen(p.id)} aria-label={'Open booking payment ' + p.reference}>Open</Btn>
              </td>
            </tr>
          ))}
        </Table>
        )}
        {!loading && !error && payments.length === 0 && (
          <p style={{ ...noteStyle, textAlign: 'center', padding: '40px 16px' }}>
            {refundOnly ? 'No booking payment needs a refund by hand.' : 'No booking payments yet.'}
          </p>
        )}
      </div>

      <div style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', justifyContent: 'space-between', gap: 12, marginTop: 16 }}>
        <p style={noteStyle} aria-live="polite">
          {loading ? 'Loading booking payments…' : total > 0 ? `Showing ${firstRow}–${lastRow} of ${total} booking payments` : ''}
        </p>
        {total > pageSize && (
          <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
            <Btn size="sm" variant="subtle" onClick={() => setPage(page - 1)} disabled={loading || page <= 1}>Previous</Btn>
            <span style={{ ...noteStyle, color: T.walnut }}>Page {page} of {lastPage}</span>
            <Btn size="sm" variant="subtle" onClick={() => setPage(page + 1)} disabled={loading || page >= lastPage}>Next</Btn>
          </div>
        )}
      </div>
    </div>
  );
}

function Fact({ label, children }) {
  return (
    <div style={{ minWidth: 0 }}>
      <dt style={{ fontFamily: FONT, fontSize: type.caption, fontWeight: 700, color: T.driftwood, textTransform: 'uppercase', letterSpacing: 0.8 }}>{label}</dt>
      <dd style={{ fontFamily: FONT, fontSize: type.body, color: T.espresso, margin: '4px 0 0', overflowWrap: 'anywhere' }}>{children}</dd>
    </div>
  );
}

function BookingPaymentDetail({ id, onBack }) {
  const { data: p, loading, error, reload } = useAdminGet('/admin/booking-payments/' + encodeURIComponent(id), DETAIL_FAILED);
  const isMobile = useIsMobile();

  return (
    <div>
      <div style={{ marginBottom: 16 }}>
        <Btn size="sm" variant="subtle" onClick={onBack}>← All booking payments</Btn>
      </div>

      {error && <ErrorBox message={error} onRetry={reload} />}
      {loading && <p style={noteStyle} aria-live="polite">Loading booking payment…</p>}

      {p && !loading && (
        <>
          <section aria-labelledby="booking-payment-heading" style={{ ...cardStyle, padding: 20, marginBottom: 16 }}>
            <h2 id="booking-payment-heading" style={{ fontFamily: "'Lora', serif", fontWeight: 700, fontSize: type.h4, color: T.espresso, margin: '0 0 16px', overflowWrap: 'anywhere' }}>
              Booking payment {p.reference}
            </h2>
            {p.refundLabel && (
              <div role="note" style={{ marginBottom: 16 }}>
                <RefundFlag label={p.refundLabel} />
              </div>
            )}
            <dl style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))', gap: 16, margin: 0 }}>
              <Fact label="Reference"><span style={{ fontFamily: 'monospace' }}>{p.reference}</span></Fact>
              <Fact label="Student">{p.student?.name}</Fact>
              <Fact label="Amount">{pesos(p.amount)}</Fact>
              <Fact label="Received">{pesos(p.received)}</Fact>
              <Fact label="Outcome"><Badge variant={OUTCOME_BADGE[p.outcome] || 'subtle'}>{OUTCOME_LABEL[p.outcome] || p.outcome}</Badge></Fact>
              <Fact label="Paid at">{p.paidAt ? formatInTz(p.paidAt, ADMIN_TZ) : '—'}</Fact>
            </dl>
          </section>

          <section aria-labelledby="booking-payment-bookings" style={{ ...cardStyle, overflow: 'hidden' }}>
            <h3 id="booking-payment-bookings" style={{ fontFamily: FONT, fontWeight: 700, fontSize: type.lead, color: T.espresso, margin: 0, padding: '16px 16px 12px' }}>
              Bookings this payment covers
            </h3>
            {isMobile ? (p.bookings || []).map((b) => (
              <article key={b.bookingId} aria-label={b.title} style={{ padding: 16, borderTop: '1px solid ' + T.border, display: 'flex', flexDirection: 'column', gap: 8 }}>
                <div style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', justifyContent: 'space-between', gap: 8 }}>
                  <span style={{ fontFamily: FONT, fontWeight: 600, fontSize: type.body, color: T.espresso }}>{b.title}</span>
                  <Badge variant={STATUS_BADGE[b.status] || 'subtle'}>{b.status}</Badge>
                </div>
                <p style={{ ...noteStyle, color: T.walnut }}>{b.instructorName} · {pesos(b.price)}</p>
                <p style={{ ...noteStyle, fontSize: type.label }}>{formatInTz(b.startAt, ADMIN_TZ)}</p>
                <RefundFlag label={b.refundLabel} />
              </article>
            )) : (
            <Table headers={['Session', 'Instructor', 'Session date', 'Price', 'Status', 'Refund']} minWidth={760}>
              {(p.bookings || []).map((b) => (
                <tr key={b.bookingId} style={{ borderBottom: '1px solid ' + T.border }}>
                  <td style={{ ...cellStyle, fontWeight: 600, color: T.espresso }}>{b.title}</td>
                  <td style={cellStyle}>{b.instructorName}</td>
                  <td style={{ ...cellStyle, fontSize: type.label, color: T.driftwood }}>{formatInTz(b.startAt, ADMIN_TZ)}</td>
                  <td style={cellStyle}>{pesos(b.price)}</td>
                  <td style={cellStyle}><Badge variant={STATUS_BADGE[b.status] || 'subtle'}>{b.status}</Badge></td>
                  <td style={{ ...cellStyle, minWidth: 180 }}><RefundFlag label={b.refundLabel} /></td>
                </tr>
              ))}
            </Table>
            )}
          </section>
        </>
      )}
    </div>
  );
}
